// AdminSettings — Platform settings
// Sections: General, Fees, Limits, KYC rules, Notifications, Integrations, Team

import { useState } from 'react';

export function AdminSettings() {
  const [tab, setTab] = useState<'general' | 'fees' | 'limits' | 'kyc' | 'integrations' | 'team'>('general');
  const [fees, setFees] = useState({ beginner: 79, mid: 149, pro: 249, withdraw: 0, ach: 0, wire: 25 });
  const [limits, setLimits] = useState({ dailyLoss: 5, maxDD: 5, maxTrades: 50, maxLoginAttempts: 5 });
  const [saved, setSaved] = useState(false);

  function save() { setSaved(true); setTimeout(() => setSaved(false), 2000); }

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div>
        <h1 className="text-[22px] font-bold text-white">Settings</h1>
        <p className="text-[12px] text-white/50">Platform configuration</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-white/5" data-testid="settings-tabs">
        {(['general', 'fees', 'limits', 'kyc', 'integrations', 'team'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`stab-${t}`} className={['shrink-0 px-3 py-2 text-[12px] font-semibold capitalize border-b-2 transition', tab === t ? 'border-[#FF3B30] text-white' : 'border-transparent text-white/50 active:text-white'].join(' ')}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Platform name</label>
            <input defaultValue="Funder Pro" data-testid="set-platform-name" className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Support email</label>
            <input defaultValue="support@funderpro.live" data-testid="set-support-email" className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Maintenance mode</label>
            <label className="flex items-center gap-2 text-[13px] text-white"><input type="checkbox" className="accent-[#FF3B30]" />Enable maintenance banner</label>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Maintenance message</label>
            <textarea rows={3} defaultValue="We'll be back in 30 minutes. Trading is temporarily paused for maintenance." className="w-full rounded-[8px] bg-black/40 border border-white/10 p-3 text-[13px] text-white focus:outline-none resize-none" />
          </div>
        </div>
      )}

      {tab === 'fees' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-3">
          <h2 className="text-[13px] font-bold text-white mb-1">Tier activation fees</h2>
          <div className="grid grid-cols-3 gap-2">
            <FeeField label="Beginner"     value={fees.beginner} onChange={(v) => setFees({ ...fees, beginner: v })} testid="fee-beginner" />
            <FeeField label="Mid"          value={fees.mid}      onChange={(v) => setFees({ ...fees, mid: v })} testid="fee-mid" />
            <FeeField label="Professional" value={fees.pro}      onChange={(v) => setFees({ ...fees, pro: v })} testid="fee-pro" />
          </div>
          <h2 className="text-[13px] font-bold text-white mb-1 mt-3">Withdrawal fees</h2>
          <div className="grid grid-cols-3 gap-2">
            <FeeField label="ACH"    value={fees.ach}    onChange={(v) => setFees({ ...fees, ach: v })} testid="fee-ach" />
            <FeeField label="Wire"   value={fees.wire}   onChange={(v) => setFees({ ...fees, wire: v })} testid="fee-wire" />
            <FeeField label="Crypto" value={fees.withdraw} onChange={(v) => setFees({ ...fees, withdraw: v })} testid="fee-crypto" />
          </div>
        </div>
      )}

      {tab === 'limits' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-3">
          <h2 className="text-[13px] font-bold text-white">Trading limits</h2>
          {[
            { k: 'dailyLoss', label: 'Daily loss limit %', sub: 'Auto-stop if hit' },
            { k: 'maxDD', label: 'Max drawdown %', sub: 'EOD balance' },
            { k: 'maxTrades', label: 'Max concurrent trades', sub: 'Per challenge' },
            { k: 'maxLoginAttempts', label: 'Max login attempts', sub: 'Lockout' },
          ].map((l) => (
            <div key={l.k}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-[12px] text-white">{l.label}</div>
                  <div className="text-[10px] text-white/50">{l.sub}</div>
                </div>
                <div className="text-[13px] font-bold text-white tabular-nums">{limits[l.k as keyof typeof limits]}</div>
              </div>
              <input type="range" min={1} max={l.k === 'maxTrades' ? 200 : 20} value={limits[l.k as keyof typeof limits]} onChange={(e) => setLimits({ ...limits, [l.k]: Number(e.target.value) })} data-testid={`limit-${l.k}`} className="w-full accent-[#FF3B30]" />
            </div>
          ))}
        </div>
      )}

      {tab === 'kyc' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-3">
          <h2 className="text-[13px] font-bold text-white">KYC rules</h2>
          <div className="space-y-2">
            {[
              { label: 'Auto-approve KYC for high-score submissions (90+)', v: true },
              { label: 'Require address verification', v: true },
              { label: 'Block users from sanctioned countries', v: true },
              { label: 'Allow crypto-only KYC (no address)', v: false },
              { label: 'Require SSN for US users', v: true },
              { label: 'Manual review for payouts > $5,000', v: true },
            ].map((s, i) => (
              <label key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
                <span className="text-[13px] text-white">{s.label}</span>
                <input type="checkbox" defaultChecked={s.v} data-testid={`kycrule-${i}`} className="accent-[#FF3B30] h-5 w-5" />
              </label>
            ))}
          </div>
        </div>
      )}

      {tab === 'integrations' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-2">
          {[
            { name: 'Stripe',     status: 'connected', env: 'live' },
            { name: 'Persona',    status: 'connected', env: 'live' },
            { name: 'TwelveData', status: 'connected', env: 'live' },
            { name: 'Rithmic',    status: 'connected', env: 'live' },
            { name: 'TradingView',status: 'connected', env: 'live' },
            { name: 'Twilio',     status: 'connected', env: 'live' },
            { name: 'Coinbase',   status: 'connected', env: 'live' },
            { name: 'Cloudflare', status: 'connected', env: 'live' },
          ].map((i) => (
            <div key={i.name} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0" data-testid={`integ-${i.name}`}>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-white/5 text-[14px]">⚡</div>
                <div>
                  <div className="text-[13px] font-semibold text-white">{i.name}</div>
                  <div className="text-[10px] text-white/50 uppercase">Env: {i.env}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#34C759]" />
                <span className="text-[11px] font-semibold text-[#34C759]">{i.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'team' && (
        <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4 space-y-2">
          {[
            { name: 'Super Admin',  email: 'admin@funderpro.live',     role: 'Owner',     last: 'now' },
            { name: 'Sofia Petrov',  email: 'sofia@funderpro.live',     role: 'Moderator', last: '5m ago' },
            { name: 'Marcus Chen',   email: 'marcus@funderpro.live',    role: 'Support',   last: '2h ago' },
            { name: 'Aiden Wright',  email: 'aiden@funderpro.live',     role: 'KYC Reviewer', last: '1d ago' },
          ].map((m) => (
            <div key={m.email} data-testid={`team-${m.email}`} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#FF3B30] to-[#FF9500] text-white text-[12px] font-bold">{m.name.split(' ').map((p) => p[0]).join('')}</div>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-white">{m.name}</div>
                <div className="text-[10px] text-white/50">{m.email}</div>
              </div>
              <div className="text-right">
                <span className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white/80">{m.role}</span>
                <div className="text-[10px] text-white/50 mt-0.5">last {m.last}</div>
              </div>
            </div>
          ))}
          <button onClick={() => alert('Invite admin')} data-testid="invite-admin" className="mt-2 flex h-10 w-full items-center justify-center rounded-[8px] bg-white/5 text-[12px] font-semibold text-white active:bg-white/10">+ Invite admin</button>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} data-testid="save-settings" className="h-11 px-6 rounded-full bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-[14px] font-bold text-white active:opacity-60">
          {saved ? '✓ Saved' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}

function FeeField({ label, value, onChange, testid }: { label: string; value: number; onChange: (v: number) => void; testid?: string }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">{label}</label>
      <div className="flex items-center gap-1">
        <span className="text-[14px] text-white/50">$</span>
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-2 text-[14px] font-bold text-white focus:outline-none" />
      </div>
    </div>
  );
}
